import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can as canDo, type LoginInput, type Permission } from '@oficina/shared';
import { ApiError, onUnauthorized } from '@/lib/api';
import { qk } from '@/lib/query-keys';
import { authService } from '@/services/auth.service';
import type { User } from '@/types/api';

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  login: (input: LoginInput) => Promise<User>;
  logout: () => Promise<void>;
  can: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const me = useQuery({
    queryKey: qk.me,
    queryFn: async () => {
      try {
        return await authService.me();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  // Limpa a sessão no cliente: marca "sem usuário" (o RequireAuth redireciona para /login)
  // e descarta os demais dados. Não usar qc.clear(): ele remove a query do usuário por baixo
  // dos componentes montados, que continuam exibindo o usuário antigo e não redirecionam.
  const clearSession = useCallback(async () => {
    await qc.cancelQueries();
    qc.setQueryData(qk.me, null);
    qc.removeQueries({ predicate: (q) => !(q.queryKey[0] === qk.me[0] && q.queryKey[1] === qk.me[1]) });
  }, [qc]);

  // Sessão expirou em qualquer chamada → volta para o login.
  useEffect(() => onUnauthorized(() => void clearSession()), [clearSession]);

  const loginMutation = useMutation({
    mutationFn: authService.login,
    onSuccess: ({ user }) => qc.setQueryData(qk.me, user),
  });

  const logout = useCallback(async () => {
    await authService.logout().catch(() => undefined);
    await clearSession();
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: me.data ?? null,
      isLoading: me.isLoading,
      login: async (input) => (await loginMutation.mutateAsync(input)).user,
      logout,
      can: (permission) => canDo(me.data?.role, permission),
    }),
    [me.data, me.isLoading, loginMutation, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth precisa estar dentro de <AuthProvider>');
  return ctx;
}
